'use client';

import '@vidstack/react/player/styles/base.css';
import '@vidstack/react/player/styles/default/theme.css';
import '@vidstack/react/player/styles/default/layouts/audio.css';
import '@vidstack/react/player/styles/default/layouts/video.css';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  MediaPlayer,
  MediaProvider,
  Poster,
  Track,
  TimeSlider,
  type MediaPlayerInstance,
  Captions,
  CaptionButton,
  useMediaState,
} from '@vidstack/react';
import {
  DefaultAudioLayout,
  DefaultVideoLayout,
  defaultLayoutIcons,
  type DefaultVideoLayoutSlots,
  DefaultTooltip,
  useDefaultLayoutContext,
  useDefaultLayoutWord,
} from '@vidstack/react/player/layouts/default';
import { cleanAudioUrl } from '@/lib/audio-proxy-utils';
import { mediaCueRegistry } from '@/lib/media-cue-registry';
import {
  BOTTOM_PLAYER_TIME_SLIDER_CONTAINER_ID,
  BOTTOM_PLAYER_TIME_SLIDER_ELEMENT_ID,
  BOTTOM_PLAYER_TIME_SLIDER_CONTAINER_READY_EVENT,
  BOTTOM_PLAYER_TIME_SLIDER_MOUNTED_EVENT,
  BOTTOM_PLAYER_TIME_SLIDER_UNMOUNTED_EVENT,
  BOTTOM_PLAYER_VARIANT_CHANGED_EVENT,
} from '@/components/episode/BottomPlayerControls';
import {
  GLOBAL_PLAYER_TIME_SLIDER_CONTAINER_ID,
  GLOBAL_PLAYER_TIME_SLIDER_ELEMENT_ID,
  GLOBAL_PLAYER_TIME_SLIDER_CONTAINER_READY_EVENT,
  GLOBAL_PLAYER_TIME_SLIDER_MOUNTED_EVENT,
  GLOBAL_PLAYER_TIME_SLIDER_UNMOUNTED_EVENT,
} from '@/components/player/time-slider-constants';

type SliderInlineStyle = React.CSSProperties & {
  [name: `--${string}`]: string | number | null | undefined;
};

const CAPTION_TRACK_KINDS = new Set(['captions', 'subtitles']);
const YOUTUBE_ID_REGEX =
  /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;

const extractYouTubeVideoId = (value?: string | null) => {
  if (!value) {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  if (/^[A-Za-z0-9_-]{11}$/.test(trimmed)) {
    return trimmed;
  }
  if (trimmed.startsWith('youtube/')) {
    const [, id] = trimmed.split('/', 2);
    return id?.trim() || null;
  }
  const match = trimmed.match(YOUTUBE_ID_REGEX);
  return match?.[1] ?? null;
};

const ImmersiveControls = React.memo(
  ({ immersiveSlot }: { immersiveSlot?: React.ReactNode }) => {
    if (!immersiveSlot) return null;
    return <div className="flex items-center gap-1 px-2">{immersiveSlot}</div>;
  }
);
ImmersiveControls.displayName = "ImmersiveControls";

const SmallLayoutCaptionButton = React.memo(() => {
  const { icons: Icons } = useDefaultLayoutContext();
  const captionsLabel = useDefaultLayoutWord('Captions');
  const captionsOnText = useDefaultLayoutWord('Closed-Captions On');
  const captionsOffText = useDefaultLayoutWord('Closed-Captions Off');
  const activeTrack = useMediaState('textTrack') as TextTrack | null;
  const activeKind = activeTrack?.kind?.toLowerCase() ?? '';
  const isActive = Boolean(activeTrack && CAPTION_TRACK_KINDS.has(activeKind));
  const tooltipText = isActive ? captionsOnText : captionsOffText;

  return (
    <DefaultTooltip content={tooltipText} placement="top">
      <CaptionButton
        className="vds-caption-button vds-button"
        aria-label={captionsLabel}
      >
        {isActive ? (
          <Icons.CaptionButton.On className="vds-icon" />
        ) : (
          <Icons.CaptionButton.Off className="vds-icon" />
        )}
      </CaptionButton>
    </DefaultTooltip>
  );
});
SmallLayoutCaptionButton.displayName = 'SmallLayoutCaptionButton';

interface VideoPlayerVidstackProps {
  url: string;
  className?: string;
  isPlaying?: boolean;
  currentTime?: number;
  thumbnailUrl?: string;
  onPlayPause?: (isPlaying: boolean) => void;
  onTimeUpdate?: (currentTime: number) => void;
  onDurationChange?: (duration: number) => void;
  onSeekTo?: (player: {
    seekTo: (time: number) => void;
    setPlaybackRate: (speed: number) => void;
    getCurrentTime: () => number;
    play: () => void;
    pause: () => void;
  }) => void;
  onEnded?: () => void;
  title?: string;
  artist?: string;
  album?: string;
  artwork?: Array<{
    src: string;
    sizes: string;
    type: string;
  }>;
  textTracks?: Array<{
    src: string;
    kind?: 'subtitles' | 'captions' | 'descriptions' | 'chapters' | 'metadata';
    label?: string;
    srclang?: string;
    default?: boolean;
  }>;
  layoutThumbnails?: string;
  videoLayoutSlots?: Partial<DefaultVideoLayoutSlots>;
  immersiveSlot?: React.ReactNode;
  muted?: boolean;
  enableTapToggle?: boolean;
  maintainAspectRatio?: boolean;
  borderRadius?: string;
}

/**
 * Experimental Vidstack-backed video player. Mirrors the props exposed by the legacy
 * `VideoPlayer` component so it can be dropped in via a feature flag without
 * touching the surrounding player logic.
 */
export function VideoPlayerVidstack({
  url,
  className,
  isPlaying,
  currentTime,
  thumbnailUrl,
  onPlayPause,
  onTimeUpdate,
  onDurationChange,
  onSeekTo,
  onEnded,
  title,
  artist,
  album,
  artwork,
  textTracks,
  layoutThumbnails,
  videoLayoutSlots,
  immersiveSlot,
  muted = false,
  enableTapToggle = false,
  maintainAspectRatio = true,
  borderRadius = '0px',
}: VideoPlayerVidstackProps) {
  const playerRef = useRef<MediaPlayerInstance | null>(null);
  const mediaElementRef = useRef<HTMLMediaElement | null>(null);
  const isApplyingExternalTime = useRef(false);
  const hasAppliedInitialTime = useRef(false);
  const pendingInitialTime = useRef<number | undefined>(currentTime);
  const playStartTimeRef = useRef<number | null>(null);
  const hasStartedPlaybackOnce = useRef(false);
  
  // 在组件首次挂载时记录初始 currentTime
  useEffect(() => {
    if (currentTime && currentTime > 0) {
      pendingInitialTime.current = currentTime;
      hasAppliedInitialTime.current = false;
      hasStartedPlaybackOnce.current = false;
    }
  }, []);

  const computedAspectRatioClassName = maintainAspectRatio
    ? 'relative w-full bg-transparent'
    : 'relative flex items-center justify-center h-full bg-transparent';

  const containerStyle = maintainAspectRatio
    ? ({ aspectRatio: '16 / 9', borderRadius, overflow: 'hidden' } as React.CSSProperties)
    : ({ maxHeight: '100%', maxWidth: '100%', borderRadius, overflow: 'hidden' } as React.CSSProperties);

  const playerWrapperClassName = maintainAspectRatio
    ? 'absolute inset-0 h-full w-full'
    : 'relative flex h-full w-full items-center justify-center';

  const providerClassName = maintainAspectRatio
    ? 'h-full w-full'
    : 'relative h-auto w-auto max-h-full max-w-full';

  const mediaElementClassName = maintainAspectRatio
    ? 'absolute inset-0 h-full w-full object-cover'
    : 'relative h-auto w-auto max-h-full max-w-full object-contain';

  const playerStyle = useMemo(
    () =>
      ({
        '--media-object-fit': maintainAspectRatio ? 'cover' : 'contain',
        '--video-bg': 'transparent',
        '--video-border': 'none',
        '--video-border-radius': borderRadius,
        '--video-shadow': 'none',
      }) as React.CSSProperties,
    [maintainAspectRatio, borderRadius],
  );

  const {
    descriptor: sourceDescriptor,
    isVideoSource,
    key: sourceKey,
  } = useMemo(() => {
    if (!url) {
      return {
        descriptor: undefined,
        isVideoSource: false,
        key: '',
      };
    }

    const youtubeId = extractYouTubeVideoId(url);
    if (youtubeId) {
      const src = `youtube/${youtubeId}`;

      return {
        descriptor: src,
        isVideoSource: true,
        key: src,
      };
    }

    if (typeof url === 'string' && url.startsWith('youtube/')) {
      return {
        descriptor: url,
        isVideoSource: true,
        key: url,
      };
    }

    const cleaned = cleanAudioUrl(url);

    const extensionMatch = url.split('?')[0]?.match(/\.([a-z0-9]+)$/i);
    const extension = extensionMatch?.[1]?.toLowerCase() ?? '';

    const videoTypes: Record<string, string> = {
      mp4: 'video/mp4',
      m4v: 'video/mp4',
      mov: 'video/quicktime',
      webm: 'video/webm',
      mkv: 'video/x-matroska',
      m3u8: 'application/vnd.apple.mpegurl',
      mpd: 'application/dash+xml',
    };

    const audioTypes: Record<string, string> = {
      mp3: 'audio/mpeg',
      m4a: 'audio/mp4',
      aac: 'audio/aac',
      wav: 'audio/wav',
      ogg: 'audio/ogg',
      oga: 'audio/ogg',
      flac: 'audio/flac',
    };

    if (extension && videoTypes[extension]) {
      return {
        descriptor: { src: cleaned, type: videoTypes[extension] },
        isVideoSource: true,
        key: cleaned,
      };
    }

    if (extension && audioTypes[extension]) {
      return {
        descriptor: { src: cleaned, type: audioTypes[extension] },
        isVideoSource: false,
        key: cleaned,
      };
    }

    // Handle blob.vercel-storage.com URLs as video/mp4 (downloaded YouTube videos)
    if (cleaned.includes('blob.vercel-storage.com')) {
      return {
        descriptor: { src: cleaned, type: 'video/mp4' },
        isVideoSource: true,
        key: cleaned,
      };
    }

    return {
      descriptor: cleaned,
      isVideoSource: true,
      key: cleaned,
    };
  }, [url]);

  const ensureMediaElement = useCallback(() => {
    const player = playerRef.current;
    if (!player) {
      return null;
    }

    const host = player.el as HTMLElement | null;
    if (!host) {
      return null;
    }

    const media =
      (host.querySelector('video') as HTMLVideoElement | null) ??
      (host.querySelector('audio') as HTMLAudioElement | null);

    if (media) {
      mediaElementRef.current = media;
      if ('playsInline' in media) {
        (media as HTMLVideoElement).playsInline = true;
      }
      if ('disableRemotePlayback' in media) {
        try {
          (media as HTMLVideoElement).disableRemotePlayback = true;
        } catch {
          // ignore unsupported props
        }
      }
      media.setAttribute('playsinline', 'true');
      media.setAttribute('webkit-playsinline', 'true');
      media.setAttribute('x5-playsinline', 'true');
      media.setAttribute('data-playsinline', 'true');
      media.setAttribute(
        'controlslist',
        'nodownload nofullscreen noremoteplayback',
      );

      if (enableTapToggle && !(media as any)._vidstackTapHandler) {
        const handler = () => {
          if (media.paused) {
            media
              .play()
              .then(() => {
                onPlayPause?.(true);
              })
              .catch((error) =>
                console.error(
                  'VideoPlayerVidstack tap-to-play failed:',
                  error,
                ),
              );
          } else {
            media.pause();
            onPlayPause?.(false);
          }
        };
        (media as any)._vidstackTapHandler = handler;
        media.addEventListener('click', handler);
      }
    }

    return media;
  }, [enableTapToggle]);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;

    const handler = () => {
      ensureMediaElement();
    };

    const applyPendingTime = () => {
      // 当播放器准备好时，检查是否需要应用初始时间
      const pendingTime = pendingInitialTime.current;
      const currentPlayer = playerRef.current;
      if (pendingTime !== undefined && pendingTime > 0 && currentPlayer) {
        try {
          const currentPlayerTime = currentPlayer.currentTime ?? 0;
          const needsAdjustment = Math.abs(currentPlayerTime - pendingTime) > 1;
          
          // 只有在时间相差超过1秒时才调整
          if (needsAdjustment) {
            // 检测是否是 YouTube 视频
            const isYouTube = sourceKey.startsWith('youtube/');
            
            // YouTube 需要额外延迟来确保 API 准备好
            const applyDelay = isYouTube ? 300 : 0;
            
            setTimeout(() => {
              const playerInTimeout = playerRef.current;
              if (!playerInTimeout) return;
              try {
                isApplyingExternalTime.current = true;
                playerInTimeout.currentTime = pendingTime;
                hasAppliedInitialTime.current = true;
                setTimeout(() => {
                  isApplyingExternalTime.current = false;
                }, 150);
              } catch (error) {
                console.warn('VideoPlayerVidstack applyPendingTime error:', error);
                isApplyingExternalTime.current = false;
              }
            }, applyDelay);
          }
        } catch (error) {
          console.warn('VideoPlayerVidstack getCurrentTime in applyPendingTime error:', error);
        }
      }
    };

    const handleCanPlay = () => applyPendingTime();
    const handleLoadedMetadata = () => applyPendingTime();

    player.addEventListener('provider-setup', handler as EventListener);
    player.addEventListener('can-play', handleCanPlay as EventListener);
    player.addEventListener('loaded-metadata', handleLoadedMetadata as EventListener);
    handler();
    return () => {
      player.removeEventListener('provider-setup', handler as EventListener);
      player.removeEventListener('can-play', handleCanPlay as EventListener);
      player.removeEventListener('loaded-metadata', handleLoadedMetadata as EventListener);
    };
  }, [ensureMediaElement, sourceKey]);

  useEffect(() => {
    const media = ensureMediaElement();
    if (!media) {
      return;
    }

    // 当 source 改变时，重置初始时间标志
    hasAppliedInitialTime.current = false;
    hasStartedPlaybackOnce.current = false;
    // 🔥 切换视频时从 0 开始，不使用之前视频的 currentTime
    pendingInitialTime.current = 0;
    playStartTimeRef.current = null;

    const reportMediaState = () => {
      // Media state reporting for debugging if needed
    };

    const mediaKind = media?.tagName === 'AUDIO' ? 'audio' : 'video';
    mediaCueRegistry.setActiveMediaElement(media, mediaKind);
    media.addEventListener('loadeddata', reportMediaState);
    media.addEventListener('loadedmetadata', reportMediaState);
    reportMediaState();
    return () => {
      mediaCueRegistry.clearActiveMediaElement(media);
      const tapHandler = (media as any)._vidstackTapHandler as
        | EventListener
        | undefined;
      if (tapHandler) {
        media.removeEventListener('click', tapHandler);
        delete (media as any)._vidstackTapHandler;
      }
      media.removeEventListener('loadeddata', reportMediaState);
      media.removeEventListener('loadedmetadata', reportMediaState);
    };
  }, [ensureMediaElement, sourceKey, currentTime]);

  // 🔥 当 sourceKey 变化时（切换视频），需要重新注册控制器
  // 使用 ref 来确保控制器始终指向最新的 playerRef
  const controllerRef = useRef<{
    seekTo: (time: number) => void;
    setPlaybackRate: (speed: number) => void;
    getCurrentTime: () => number;
    play: () => Promise<void>;
    pause: () => Promise<void>;
  } | null>(null);

  useEffect(() => {
    const controller = {
      seekTo: (time: number) => {
        const p = playerRef.current;
        if (!p) return;
        try {
          p.currentTime = Number.isFinite(time) ? time : (p.currentTime ?? 0);
        } catch (error) {
          console.warn('VideoPlayerVidstack seekTo error:', error);
        }
      },
      setPlaybackRate: (speed: number) => {
        const p = playerRef.current;
        if (!p) return;
        try {
          p.playbackRate = speed;
        } catch (error) {
          console.warn('VideoPlayerVidstack setPlaybackRate error:', error);
        }
      },
      getCurrentTime: () => {
        const p = playerRef.current;
        if (!p) return 0;
        try {
          return p.currentTime ?? 0;
        } catch (error) {
          console.warn('VideoPlayerVidstack getCurrentTime error:', error);
          return 0;
        }
      },
      play: () => {
        const p = playerRef.current;
        if (!p) return Promise.resolve();
        try {
          return p.play() ?? Promise.resolve();
        } catch (error) {
          console.warn('VideoPlayerVidstack play error:', error);
          return Promise.resolve();
        }
      },
      pause: () => {
        const p = playerRef.current;
        if (!p) return Promise.resolve();
        try {
          return p.pause() ?? Promise.resolve();
        } catch (error) {
          console.warn('VideoPlayerVidstack pause error:', error);
          return Promise.resolve();
        }
      },
    };

    controllerRef.current = controller;

    // 当 sourceKey 变化时，等待播放器准备好后再注册控制器
    if (onSeekTo) {
      // 对于 YouTube 视频，需要等待播放器初始化
      const isYouTube = sourceKey.startsWith('youtube/');
      const delay = isYouTube ? 500 : 100;

      const timeoutId = setTimeout(() => {
        if (playerRef.current && onSeekTo) {
          onSeekTo(controller);
        }
      }, delay);

      return () => clearTimeout(timeoutId);
    }
  }, [onSeekTo, sourceKey]);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;

    const handlePlay = () => {
      onPlayPause?.(true);
    };
    const handlePause = () => {
      onPlayPause?.(false);
    };
    const handleEnded = () => {
      onEnded?.();
    };
    // 🔋 优化：节流时间更新，减少回调频率以降低 CPU 负载
    let lastTimeUpdateCall = 0;
    const TIME_UPDATE_THROTTLE_MS = 100; // 每 100ms 最多更新一次（10fps）
    
    const handleTimeChange = (event: Event) => {
      const detail = (event as CustomEvent<number>).detail;
      if (typeof detail === 'number' && !isApplyingExternalTime.current) {
        // 防止 YouTube 播放器在播放开始后的短时间内报告错误的时间
        const pendingTime = pendingInitialTime.current;
        const playStartTime = playStartTimeRef.current;
        const player = playerRef.current;
        
        // 如果刚开始播放（2秒内），且报告的时间远小于待处理的时间，主动修正
        if (playStartTime && (Date.now() - playStartTime < 2000)) {
          if (pendingTime && pendingTime > 5 && detail < (pendingTime - 5)) {
            if (player) {
              isApplyingExternalTime.current = true;
              player.currentTime = pendingTime;
              setTimeout(() => {
                isApplyingExternalTime.current = false;
              }, 150);
            }
            return;
          }
        }
        
        // 🔋 节流：限制 onTimeUpdate 调用频率
        const now = Date.now();
        if (now - lastTimeUpdateCall >= TIME_UPDATE_THROTTLE_MS) {
          lastTimeUpdateCall = now;
          onTimeUpdate?.(detail);
        }
      }
    };
    const handleDurationChange = (event: Event) => {
      const detail = (event as CustomEvent<number>).detail;
      if (typeof detail === 'number') {
        onDurationChange?.(detail);
      }
    };

    player.addEventListener('play', handlePlay as EventListener);
    player.addEventListener('pause', handlePause as EventListener);
    player.addEventListener('ended', handleEnded as EventListener);
    player.addEventListener(
      'time-change',
      handleTimeChange as EventListener,
    );
    player.addEventListener(
      'duration-change',
      handleDurationChange as EventListener,
    );

    return () => {
      player.removeEventListener('play', handlePlay as EventListener);
      player.removeEventListener('pause', handlePause as EventListener);
      player.removeEventListener('ended', handleEnded as EventListener);
      player.removeEventListener(
        'time-change',
        handleTimeChange as EventListener,
      );
      player.removeEventListener(
        'duration-change',
        handleDurationChange as EventListener,
      );
    };
  }, [onDurationChange, onEnded, onPlayPause, onTimeUpdate, thumbnailUrl, isVideoSource]);

  // 🔥 跟踪是否需要在播放器准备好后自动播放
  const pendingPlayOnReadyRef = useRef(false);
  const lastSourceKeyRef = useRef(sourceKey);

  // 🔥 跟踪是否刚切换了视频，用于等待外部传入的初始时间
  const justSwitchedVideoRef = useRef(false);
  const switchTimeRef = useRef<number>(0);
  
  // 🔥 当 sourceKey 变化时，如果当前 isPlaying 为 true，记录需要自动播放
  useEffect(() => {
    if (lastSourceKeyRef.current !== sourceKey) {
      lastSourceKeyRef.current = sourceKey;
      // 重置播放状态标志
      hasStartedPlaybackOnce.current = false;
      hasAppliedInitialTime.current = false;
      playStartTimeRef.current = null;
      // 🔥 标记刚切换了视频，等待外部传入初始时间
      justSwitchedVideoRef.current = true;
      switchTimeRef.current = Date.now();
      // 先设置为 0，如果有历史记录时间会在后续被更新
      pendingInitialTime.current = 0;
      
      // 如果切换视频时 isPlaying 为 true，标记需要自动播放
      if (isPlaying) {
        pendingPlayOnReadyRef.current = true;
        console.log('[VideoPlayerVidstack] Source changed while playing, will auto-play when ready');
      }
      
      // 300ms 后清除"刚切换视频"标志，此时应该已经收到正确的初始时间
      setTimeout(() => {
        justSwitchedVideoRef.current = false;
      }, 300);
    }
  }, [sourceKey, isPlaying]);

  // 🔥 监听播放器的 can-play 事件，处理切换视频后的自动播放
  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;

    const handleCanPlayForAutoStart = () => {
      if (pendingPlayOnReadyRef.current) {
        pendingPlayOnReadyRef.current = false;
        console.log('[VideoPlayerVidstack] Player ready, triggering auto-play');
        
        const pendingTime = pendingInitialTime.current;
        const isYouTube = sourceKey.startsWith('youtube/');
        
        // 设置初始时间
        if (pendingTime !== undefined && pendingTime > 0) {
          try {
            isApplyingExternalTime.current = true;
            player.currentTime = pendingTime;
            hasAppliedInitialTime.current = true;
          } catch (error) {
            console.warn('[VideoPlayerVidstack] Failed to set initial time:', error);
          }
          
          setTimeout(() => {
            isApplyingExternalTime.current = false;
          }, 150);
        }
        
        // 延迟后播放
        const playDelay = isYouTube ? 800 : 100;
        setTimeout(() => {
          const p = playerRef.current;
          if (!p) return;
          
          hasStartedPlaybackOnce.current = true;
          playStartTimeRef.current = Date.now();
          
          p.play().catch((error) => {
            console.error('[VideoPlayerVidstack] Auto-play failed:', error);
          });
        }, playDelay);
      }
    };

    player.addEventListener('can-play', handleCanPlayForAutoStart as EventListener);
    
    return () => {
      player.removeEventListener('can-play', handleCanPlayForAutoStart as EventListener);
    };
  }, [sourceKey]);

  useEffect(() => {
    const player = playerRef.current;
    if (!player || typeof isPlaying !== 'boolean') return;

    // 🔥 如果是因为 sourceKey 变化触发的，且已经设置了 pendingPlayOnReady，跳过这次
    // 让 can-play 事件处理器来处理播放
    if (pendingPlayOnReadyRef.current && isPlaying) {
      console.log('[VideoPlayerVidstack] Skipping play, waiting for can-play event');
      return;
    }

    if (isPlaying) {
      const pendingTime = pendingInitialTime.current;
      let currentPlayerTime = 0;
      try {
        currentPlayerTime = player.currentTime ?? 0;
      } catch (error) {
        console.warn('VideoPlayerVidstack getCurrentTime error in isPlaying effect:', error);
      }
      const isYouTube = sourceKey.startsWith('youtube/');
      
      // 对于所有视频，如果有待处理的初始时间且还没真正播放过，
      // 总是强制设置时间，确保从正确位置开始播放
      const shouldForceTimeForFirstPlay = 
        pendingTime !== undefined && 
        pendingTime > 0 &&
        !hasStartedPlaybackOnce.current;
      
      const needsTimeAdjustment = 
        (pendingTime !== undefined && 
         pendingTime > 0 && 
         Math.abs(currentPlayerTime - pendingTime) > 1) ||
        shouldForceTimeForFirstPlay;
      
      if (needsTimeAdjustment) {
        const isBlobVideo = sourceKey.includes('blob.vercel-storage.com');
        
        try {
          isApplyingExternalTime.current = true;
          player.currentTime = pendingTime!;
          hasAppliedInitialTime.current = true;
          playStartTimeRef.current = Date.now();
        } catch (error) {
          console.warn('VideoPlayerVidstack setCurrentTime error:', error);
          isApplyingExternalTime.current = false;
        }
        
        // YouTube 需要更长的延迟来确保 seek 完成，blob 视频需要中等延迟
        const playDelay = isYouTube ? 1200 : (isBlobVideo ? 500 : 150);
        
        setTimeout(() => {
          isApplyingExternalTime.current = false;
        }, 150);
        
        // 延迟后开始播放，确保时间设置生效
        setTimeout(() => {
          const playerInTimeout = playerRef.current;
          if (!playerInTimeout) return;
          
          hasStartedPlaybackOnce.current = true;
          
          playerInTimeout.play().catch((error) => {
            console.error('VideoPlayerVidstack play failed:', error);
          });
          
          // 对于 YouTube，播放开始后再次验证并强制设置时间
          if (isYouTube && pendingTime) {
            setTimeout(() => {
              const playerInNestedTimeout = playerRef.current;
              if (!playerInNestedTimeout) return;
              
              try {
                const actualTime = playerInNestedTimeout.currentTime ?? 0;
                const timeDiff = Math.abs(actualTime - pendingTime);
                
                // 如果时间差异超过 2 秒，强制再次设置
                if (timeDiff > 2) {
                  isApplyingExternalTime.current = true;
                  playerInNestedTimeout.currentTime = pendingTime;
                  setTimeout(() => {
                    isApplyingExternalTime.current = false;
                  }, 150);
                }
              } catch (error) {
                console.warn('VideoPlayerVidstack nested timeout error:', error);
                isApplyingExternalTime.current = false;
              }
            }, 200);
          }
        }, playDelay);
      } else {
        playStartTimeRef.current = Date.now();
        player.play().catch((error) => {
          console.error('VideoPlayerVidstack play failed:', error);
        });
      }
    } else {
      pendingPlayOnReadyRef.current = false; // 清除待播放标志
      playStartTimeRef.current = null;
      player.pause().catch((error) => {
        console.error('VideoPlayerVidstack pause failed:', error);
      });
    }
  }, [isPlaying, sourceKey]);

  // 🔥 跟踪上一次的 sourceKey，用于判断是否刚切换了视频
  const prevSourceKeyForTimeSync = useRef(sourceKey);
  
  useEffect(() => {
    if (typeof currentTime !== 'number') return;
    const player = playerRef.current;
    if (!player) return;

    // 🔥 如果视频源刚刚变化
    if (prevSourceKeyForTimeSync.current !== sourceKey) {
      prevSourceKeyForTimeSync.current = sourceKey;
      // 如果在切换视频后的短时间窗口内收到了非零的 currentTime，这可能是历史记录时间
      // 记录下来用于初始播放位置
      if (justSwitchedVideoRef.current && currentTime > 0) {
        console.log('[VideoPlayerVidstack] Received initial time from history:', currentTime);
        pendingInitialTime.current = currentTime;
      }
      return;
    }

    // 🔥 在刚切换视频的时间窗口内，接受外部传入的初始时间（可能是历史记录）
    if (justSwitchedVideoRef.current && currentTime > 0) {
      console.log('[VideoPlayerVidstack] Received initial time from history:', currentTime);
      pendingInitialTime.current = currentTime;
      return;
    }

    // 正常的时间同步逻辑
    if (currentTime > 0) {
      pendingInitialTime.current = currentTime;
    }

    try {
      const playerCurrentTime = player.currentTime ?? 0;
      const diff = Math.abs(playerCurrentTime - currentTime);
      // 外部 currentTime 变化时同步到播放器
      if (diff > 0.25) {
        isApplyingExternalTime.current = true;
        player.currentTime = currentTime;
        setTimeout(() => {
          isApplyingExternalTime.current = false;
        }, 150);
      }
    } catch (error) {
      console.warn('VideoPlayerVidstack currentTime sync error:', error);
      isApplyingExternalTime.current = false;
    }
  }, [currentTime, sourceKey]);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    player.muted = muted;
    if (muted) {
      player.volume = 0;
    }
  }, [muted]);

  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    if (!title) return;

    const metadata: MediaMetadataInit = {
      title,
      artist,
      album,
      artwork,
    };

    if ('mediaSession' in navigator) {
      try {
        navigator.mediaSession.metadata = new MediaMetadata(metadata);
      } catch (error) {
        console.error('VideoPlayerVidstack media session metadata error:', error);
      }
    }
  }, [album, artist, artwork, title]);

  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    const updateState = () => {
      const media = mediaElementRef.current;
      if (!media) return;
      const setPositionState =
        navigator.mediaSession?.setPositionState?.bind(navigator.mediaSession);
      if (!setPositionState) return;
      try {
        setPositionState({
          duration: media.duration || 0,
          position: media.currentTime || 0,
          playbackRate: media.playbackRate || 1,
        });
      } catch (error) {
        console.error('VideoPlayerVidstack media session position error:', error);
      }
    };

    const interval = setInterval(updateState, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    const media = mediaElementRef.current;
    if (!media) return;

    const updatePlaybackState = () => {
      try {
        navigator.mediaSession!.playbackState = media.paused
          ? 'paused'
          : 'playing';
      } catch (error) {
        console.error('VideoPlayerVidstack media session playback error:', error);
      }
    };

    updatePlaybackState();
    media.addEventListener('play', updatePlaybackState);
    media.addEventListener('pause', updatePlaybackState);
    return () => {
      media.removeEventListener('play', updatePlaybackState);
      media.removeEventListener('pause', updatePlaybackState);
    };
  }, [sourceKey]);

  const handleMediaClick = useCallback(() => {
    const media = mediaElementRef.current;
    if (!media) return;

    if (media.paused) {
      media
        .play()
        .then(() => {
          onPlayPause?.(true);
        })
        .catch((error) =>
          console.error('VideoPlayerVidstack tap-to-play failed:', error),
        );
      return;
    }

    if (!enableTapToggle) {
      return;
    }

    media.pause();
    onPlayPause?.(false);
  }, [enableTapToggle, onPlayPause]);

  const layoutSlots = useMemo(() => {
    const baseSlots = (videoLayoutSlots ?? {}) as Partial<DefaultVideoLayoutSlots>;
    const mergedSlots: Partial<DefaultVideoLayoutSlots> = {
      ...baseSlots,
      settingsMenu: null,
    };

    const existingTopEnd = baseSlots.topControlsGroupEnd;
    if (immersiveSlot) {
      mergedSlots.topControlsGroupEnd = (
        <>
          {existingTopEnd}
          <ImmersiveControls immersiveSlot={immersiveSlot} />
        </>
      );
    } else if (existingTopEnd) {
      mergedSlots.topControlsGroupEnd = existingTopEnd;
    }

    const baseSmallLayout = baseSlots.smallLayout ?? {};
    mergedSlots.smallLayout = {
      ...baseSmallLayout,
      settingsMenu: null,
      captionButton: null,
      beforeFullscreenButton: (
        <>
          {baseSmallLayout.beforeFullscreenButton}
          <SmallLayoutCaptionButton />
        </>
      ),
    };

    if (immersiveSlot) {
      mergedSlots.smallLayout.topControlsGroupEnd = (
        <>
          {baseSmallLayout.topControlsGroupEnd}
          <ImmersiveControls immersiveSlot={immersiveSlot} />
        </>
      );
    }

    if (baseSlots.largeLayout) {
      mergedSlots.largeLayout = {
        ...baseSlots.largeLayout,
        settingsMenu: null,
      };
      if (immersiveSlot) {
        mergedSlots.largeLayout.topControlsGroupEnd = (
          <>
            {baseSlots.largeLayout.topControlsGroupEnd}
            <ImmersiveControls immersiveSlot={immersiveSlot} />
          </>
        );
      }
    }

    return mergedSlots;
  }, [immersiveSlot, videoLayoutSlots]);

  const hasSource =
    typeof sourceDescriptor === 'string'
      ? Boolean(sourceDescriptor)
      : Boolean(sourceDescriptor?.src);

  if (!hasSource) {
    return (
      <div className={computedAspectRatioClassName} style={containerStyle}>
        <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
          无可用媒体
        </div>
      </div>
    );
  }

  const logLevel = process.env.NODE_ENV === 'production' ? 'error' : 'warn';

  return (
    <div className={computedAspectRatioClassName} style={containerStyle}>
      <MediaPlayer
        ref={(instance) => {
          playerRef.current = instance;
        }}
        className={`${playerWrapperClassName} ${className ?? ''} nf-hide-default-controls`}
        src={sourceDescriptor as any}
        poster={thumbnailUrl}
        style={playerStyle as any}
        playsInline
        crossOrigin
        muted={muted}
        load="eager"
        preload="metadata"
        streamType="on-demand"
        viewType={isVideoSource ? 'video' : 'audio'}
        logLevel={logLevel}
        // title={title}
        onClick={enableTapToggle ? handleMediaClick : undefined}
        controls={false}
      >
        <MediaProvider
          className={providerClassName}
          mediaProps={{
            className: mediaElementClassName,
            playsInline: true,
            crossOrigin: 'anonymous',
          } as React.VideoHTMLAttributes<HTMLVideoElement>}
        >
          {thumbnailUrl ? (
            <Poster
              className="vds-poster"
              style={{ backgroundColor: 'transparent', border: 'none', boxShadow: 'none' }}
            />
          ) : null}
          {textTracks?.map(({ srclang, ...track }) => {
            const key = `${track.src}-${track.kind ?? 'subtitles'}`;
            return (
              <Track
                key={key}
                src={track.src}
                kind={track.kind ?? 'subtitles'}
                label={track.label}
                lang={srclang}
                default={track.default}
              />
            );
          })}
      </MediaProvider>
      <Captions className="vds-captions media-captions pointer-events-none absolute bottom-3 left-1/2 z-10 flex w-full max-w-[900px] -translate-x-1/2 select-none break-words text-center text-xs font-light leading-normal text-white drop-shadow-md sm:bottom-1 sm:text-base [&_[data-part='cue-display']]:inline-block [&_[data-part='cue-display']]:rounded-md [&_[data-part='cue-display']]:px-2.5 [&_[data-part='cue-display']]:py-1.5 [&_[data-part='cue-display']]:text-white [&_[data-part='cue']]:leading-tight" />
      {isVideoSource ? (
        <DefaultVideoLayout
          icons={defaultLayoutIcons}
          thumbnails={layoutThumbnails}
          slots={layoutSlots}
      />
    ) : (
      <DefaultAudioLayout icons={defaultLayoutIcons} />
    )}
      <BottomPlayerTimeSliderPortal />
      <GlobalPlayerTimeSliderPortal />
      </MediaPlayer>
    </div>
  );
}

function BottomPlayerTimeSliderPortal() {
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const [variant, setVariant] = useState<'collapsed' | 'expanded'>('expanded');

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const updateTarget = () => {
      const el = document.getElementById(BOTTOM_PLAYER_TIME_SLIDER_CONTAINER_ID);
      if (el) {
        setTarget(el);
        const containerVariant = (el.getAttribute('data-slider-variant') as
          | 'collapsed'
          | 'expanded'
          | null) ?? 'expanded';
        setVariant(containerVariant);
      }
    };

    const handleContainerReady = () => {
      updateTarget();
    };

    const handleVariantChange = (event: Event) => {
      const detail = (event as CustomEvent<{ expanded: boolean }>).detail;
      if (detail && typeof detail.expanded === 'boolean') {
        setVariant(detail.expanded ? 'expanded' : 'collapsed');
      }
      updateTarget();
    };

    updateTarget();
    let pollId: number | null = null;

    if (!document.getElementById(BOTTOM_PLAYER_TIME_SLIDER_CONTAINER_ID)) {
      pollId = window.setInterval(() => {
        if (document.getElementById(BOTTOM_PLAYER_TIME_SLIDER_CONTAINER_ID)) {
          updateTarget();
          if (pollId !== null) {
            clearInterval(pollId);
            pollId = null;
          }
        }
      }, 150);
    }

    window.addEventListener(
      BOTTOM_PLAYER_TIME_SLIDER_CONTAINER_READY_EVENT,
      handleContainerReady,
    );
    window.addEventListener(
      BOTTOM_PLAYER_VARIANT_CHANGED_EVENT,
      handleVariantChange,
    );

    return () => {
      window.removeEventListener(
        BOTTOM_PLAYER_TIME_SLIDER_CONTAINER_READY_EVENT,
        handleContainerReady,
      );
      window.removeEventListener(
        BOTTOM_PLAYER_VARIANT_CHANGED_EVENT,
        handleVariantChange,
      );
      if (pollId !== null) {
        clearInterval(pollId);
      }
    };
  }, []);

  useEffect(() => {
    if (!target) return;
    window.dispatchEvent(new Event(BOTTOM_PLAYER_TIME_SLIDER_MOUNTED_EVENT));
    return () => {
      window.dispatchEvent(new Event(BOTTOM_PLAYER_TIME_SLIDER_UNMOUNTED_EVENT));
    };
  }, [target]);

  if (!target) {
    return null;
  }

  return createPortal(<BottomPlayerTimeSliderMarkup variant={variant} />, target);
}

function BottomPlayerTimeSliderMarkup({
  variant,
}: {
  variant: 'collapsed' | 'expanded';
}) {
  const isCollapsed = variant === 'collapsed';
  const [previewReady, setPreviewReady] = React.useState(false);
  const previewReadyRafRef = React.useRef<number | null>(null);

  const cancelPreviewReadyRaf = React.useCallback(() => {
    if (previewReadyRafRef.current !== null) {
      window.cancelAnimationFrame(previewReadyRafRef.current);
      previewReadyRafRef.current = null;
    }
  }, []);

  const schedulePreviewReady = React.useCallback(() => {
    if (previewReadyRafRef.current !== null) return;
    previewReadyRafRef.current = window.requestAnimationFrame(() => {
      previewReadyRafRef.current = null;
      setPreviewReady(true);
    });
  }, []);

  const handlePointerEnter = React.useCallback(() => {
    cancelPreviewReadyRaf();
    setPreviewReady(false);
  }, [cancelPreviewReadyRaf]);

  const handlePointerMove = React.useCallback(() => {
    schedulePreviewReady();
  }, [schedulePreviewReady]);

  const handlePointerDown = React.useCallback(() => {
    schedulePreviewReady();
  }, [schedulePreviewReady]);

  const handlePointerLeave = React.useCallback(() => {
    cancelPreviewReadyRaf();
    setPreviewReady(false);
  }, [cancelPreviewReadyRaf]);

  const handleBlur = React.useCallback(() => {
    cancelPreviewReadyRaf();
    setPreviewReady(false);
  }, [cancelPreviewReadyRaf]);

  React.useEffect(
    () => () => {
      cancelPreviewReadyRaf();
    },
    [cancelPreviewReadyRaf],
  );

  const collapsedInlineStyle = React.useMemo<SliderInlineStyle | undefined>(() => {
    if (!isCollapsed) return undefined;
    return {
      paddingTop: 0,
      paddingBottom: 0,
      alignItems: 'flex-start',
    } as SliderInlineStyle;
  }, [isCollapsed]);

  return (
    <TimeSlider.Root
      id={BOTTOM_PLAYER_TIME_SLIDER_ELEMENT_ID}
      className={`${
        isCollapsed
          ? 'px-[3px] group relative block h-[4px] w-full cursor-pointer touch-none select-none outline-none aria-hidden:hidden'
          : 'group relative mx-[7.5px] inline-flex h-10 w-full cursor-pointer touch-none select-none items-center outline-none aria-hidden:hidden'
      }`}
      style={collapsedInlineStyle}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onPointerMove={handlePointerMove}
      onPointerDown={handlePointerDown}
      onFocus={handlePointerEnter}
      onBlur={handleBlur}
    >
      <TimeSlider.Track
        className={`${
          isCollapsed
            ? 'relative z-0 h-[4px] w-full rounded-t-3xl bg-white/60'
            : 'relative z-0 h-[5px] w-full rounded-sm bg-white/30 ring-sky-400 group-data-[focus]:ring-[3px]'
        }`}
      >
        <TimeSlider.TrackFill
          className={`${
            isCollapsed
              ? 'absolute h-full w-[var(--slider-fill)] rounded-tl-3xl bg-black will-change-[width]'
              : 'absolute h-full w-[var(--slider-fill)] rounded-sm bg-black will-change-[width]'
          }`}
        />
        <TimeSlider.Progress
          className={`${
            isCollapsed
              ? 'absolute z-10 h-full w-[var(--slider-progress)] rounded-tl-3xl bg-black/40 will-change-[width]'
              : 'absolute z-10 h-full w-[var(--slider-progress)] rounded-sm bg-black/40 will-change-[width]'
          }`}
        />
      </TimeSlider.Track>
      {isCollapsed ? null : (
        <TimeSlider.Preview
          className={`vds-slider-preview pointer-events-none hidden flex-col items-center gap-1 rounded-md bg-black/80 px-2 py-1 text-xs font-medium text-white shadow-md backdrop-blur-sm transition-opacity duration-100 data-[visible]:flex ${
            previewReady ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <TimeSlider.Value className="font-mono" type="pointer" format="time" />
        </TimeSlider.Preview>
      )}
      <TimeSlider.Thumb
        className={`${
          isCollapsed
            ? 'absolute top-1/2 z-20 h-[10px] w-[10px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-black/30 bg-black opacity-0 ring-black/20 transition-opacity group-data-[active]:opacity-100 group-data-[dragging]:ring-2 will-change-[left]'
            : 'absolute top-1/2 z-20 h-[15px] w-[15px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#cacaca] bg-white opacity-0 ring-white/40 transition-opacity group-data-[active]:opacity-100 group-data-[dragging]:ring-4 will-change-[left]'
        }`}
        style={{ left: 'var(--slider-fill, 0%)' }}
      />
    </TimeSlider.Root>
  );
}

function GlobalPlayerTimeSliderPortal() {
  const [target, setTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const updateTarget = () => {
      const el = document.getElementById(
        GLOBAL_PLAYER_TIME_SLIDER_CONTAINER_ID,
      );
      setTarget((prev) => (prev === el ? prev : el));
    };

    updateTarget();

    const handleContainerReady = () => {
      updateTarget();
    };

    window.addEventListener(
      GLOBAL_PLAYER_TIME_SLIDER_CONTAINER_READY_EVENT,
      handleContainerReady,
    );

    const observer = new MutationObserver(() => {
      updateTarget();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener(
        GLOBAL_PLAYER_TIME_SLIDER_CONTAINER_READY_EVENT,
        handleContainerReady,
      );
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!target) return;
    window.dispatchEvent(new Event(GLOBAL_PLAYER_TIME_SLIDER_MOUNTED_EVENT));
    return () => {
      window.dispatchEvent(new Event(GLOBAL_PLAYER_TIME_SLIDER_UNMOUNTED_EVENT));
    };
  }, [target]);

  if (!target) {
    return null;
  }

  return createPortal(<GlobalPlayerTimeSliderMarkup />, target);
}

function GlobalPlayerTimeSliderMarkup() {
  return (
    <TimeSlider.Root
      id={GLOBAL_PLAYER_TIME_SLIDER_ELEMENT_ID}
      className="group relative flex h-10 w-full cursor-pointer select-none items-center px-0"
    >
      <TimeSlider.Track className="relative h-[5px] w-full rounded-full bg-white/20 backdrop-blur-[1px]">
        <TimeSlider.TrackFill className="z-10 absolute h-full w-[var(--slider-fill)] rounded-full bg-foreground shadow-[0_0_12px_rgba(255,255,255,0.35)] transition-[width]" />
        <TimeSlider.Progress className="absolute h-full w-[var(--slider-progress)] rounded-full bg-foreground/30 transition-[width]" />
      </TimeSlider.Track>
      <TimeSlider.Preview
        className="pointer-events-none absolute -top-8 left-[var(--slider-pointer,50%)] hidden -translate-x-1/2 flex-col items-center gap-1 text-[11px] font-medium text-white opacity-0 transition-opacity duration-100 data-[visible]:flex data-[visible]:opacity-100"
      >
        <div className="rounded-md bg-black/80 px-2 py-1 shadow-lg backdrop-blur-sm">
          <TimeSlider.Value className="font-mono" type="pointer" format="time" />
        </div>
      </TimeSlider.Preview>
      <TimeSlider.Thumb
        className="absolute top-1/2 z-10 h-[16px] w-[16px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/70 bg-white shadow-[0_4px_12px_rgba(0,0,0,0.25)] opacity-0 ring-white/50 transition-opacity duration-150 group-data-[active]:opacity-100 group-data-[dragging]:ring-4"
        style={{ left: 'var(--slider-fill, 0%)' }}
      />
    </TimeSlider.Root>
  );
}
